import { useState, useEffect, useCallback } from 'react';
import { deviceService } from '../services/deviceService';

// Custom hook for fetching all devices
export const useDevices = (homeId) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDevices = useCallback(async () => {
    if (!homeId) {
      setLoading(false);
      setDevices([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await deviceService.getAllDevices(homeId);
      setDevices(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [homeId]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const refreshDevices = () => {
    fetchDevices();
  };

  const addDevice = async (deviceData) => {
    try {
      const newDevice = await deviceService.createDevice(deviceData);
      setDevices(prev => [...prev, newDevice]);
      return newDevice;
    } catch (err) {
      throw err;
    }
  };

  const deleteDevice = async (deviceId) => {
    try {
      await deviceService.deleteDevice(deviceId);
      setDevices(prev => prev.filter(device => device.deviceId !== deviceId));
    } catch (err) {
      throw err;
    }
  };

  const toggleDevice = async (deviceId, currentIsOn) => {
    try {
      if (currentIsOn) {
        await deviceService.turnDeviceOff(deviceId);
      } else {
        await deviceService.turnDeviceOn(deviceId);
      }
      // Update the device status in local state
      setDevices(prev =>
        prev.map(device =>
          device.deviceId === deviceId
            ? { ...device, isOn: !currentIsOn }
            : device
        )
      );
    } catch (err) {
      throw err;
    }
  };

  return {
    devices,
    loading,
    error,
    refreshDevices,
    addDevice,
    deleteDevice,
    toggleDevice,
    setDevices
  };
};

// Backward compatibility
export const getDevices = useDevices;

// Hook for devices by room
export const useDevicesByRoom = (roomId) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDevices = useCallback(async () => {
    if (!roomId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await deviceService.getDevicesByRoom(roomId);
      setDevices(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  return { devices, loading, error, refreshDevices: fetchDevices };
};